import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ShoppingBag, Filter, X, AlertTriangle, TrendingUp, PackageCheck, CalendarClock,
} from 'lucide-react'
import { PageHeader } from '../../components/ui/PageHeader'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { NotAuthorizedView } from '../../components/permissions/NotAuthorizedView'
import { useCurrency } from '../../hooks/useCurrency'
import { useAuthStore } from '../../store/authStore'
import { useDebounce } from '../../hooks/useDebounce'
import { useCurrentUserPagePermissions } from '../../lib/permissions/usePagePermissions'
import { useOnlineOrderKpis } from '../../hooks/useOnlineOrderKpis'
import { fetchOnlineOrders, onlineOrderPaymentPill, type OnlineOrderStatusBucket } from '../../lib/supabaseQueries'
import { displayDateTime } from '../../utils/dates'
import { cn } from '../../utils/cn'

const STATUS_TABS: { key: OnlineOrderStatusBucket; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'paid', label: 'Paid' },
  { key: 'awaiting_fulfilment', label: 'Awaiting fulfilment' },
  { key: 'needs_attention', label: 'Needs attention' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Cancelled' },
]

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function isPickup(zone: string | null | undefined): boolean {
  return !!zone && /pick\s*-?\s*up/i.test(zone)
}

// ─── KPI row (mirrors the ReceivablesWidget card styling) ─────────────────────

function OnlineOrdersKpis({ tenantId }: { tenantId: string }) {
  const { format } = useCurrency()
  const { data, isLoading } = useOnlineOrderKpis(tenantId)

  if (isLoading || !data) return null

  const tiles = [
    { icon: <CalendarClock className="h-4 w-4 text-orange-500" />, label: 'Orders Today', value: String(data.ordersToday), accent: 'text-gray-900' },
    { icon: <TrendingUp className="h-4 w-4 text-orange-500" />, label: 'Online Revenue', sub: 'This month', value: format(data.revenueThisMonth), accent: 'text-gray-900' },
    { icon: <PackageCheck className="h-4 w-4 text-orange-500" />, label: 'Awaiting Fulfilment', value: String(data.awaitingFulfilment), accent: data.awaitingFulfilment > 0 ? 'text-blue-600' : 'text-gray-900' },
    { icon: <AlertTriangle className="h-4 w-4 text-red-500" />, label: 'Needs Attention', sub: data.stockUnavailable > 0 ? `${data.stockUnavailable} stock unavailable` : undefined, value: String(data.needsAttention), accent: data.needsAttention > 0 ? 'text-red-600' : 'text-gray-900' },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {tiles.map(t => (
        <div key={t.label} className="card border border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            {t.icon}
            <h3 className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{t.label}</h3>
          </div>
          <p className={cn('text-lg font-bold', t.accent)}>{t.value}</p>
          {t.sub && <p className="text-[10px] text-gray-400 mt-0.5">{t.sub}</p>}
        </div>
      ))}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OnlineOrdersPage() {
  const { format } = useCurrency()
  const navigate = useNavigate()
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const { getLevel } = useCurrentUserPagePermissions()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<{ from?: string; to?: string }>({})
  const [statusBucket, setStatusBucket] = useState<OnlineOrderStatusBucket>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const filters = useMemo(() => ({
    search: debouncedSearch || undefined,
    statusBucket,
    ...appliedFilters,
  }), [debouncedSearch, statusBucket, appliedFilters])

  useEffect(() => { setPage(1) }, [filters])

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['online-orders', tid, filters, page, pageSize],
    enabled: !!tid,
    queryFn: () => fetchOnlineOrders(tid, filters, { page, pageSize }),
    staleTime: 30 * 1000,
  })
  const rows = pageData?.rows ?? []
  const totalCount = pageData?.count ?? 0

  function applyFilters() {
    setAppliedFilters({ from: filterFrom || undefined, to: filterTo || undefined })
  }
  function clearFilters() {
    setFilterFrom(''); setFilterTo(''); setAppliedFilters({})
  }
  const hasFilters = !!(appliedFilters.from || appliedFilters.to)

  // Belt-and-suspenders: the route is already gated by <PermissionGuard>, but mirror
  // SalesPage and refuse to render the body for a role without at least view.
  if (getLevel('sell.online_orders') === 'none') return <NotAuthorizedView />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Online Orders"
        subtitle="Incoming orders from your storefront"
        actions={
          <button
            className={cn('btn-secondary flex items-center gap-1.5 text-xs py-1.5', hasFilters && 'border-brand-400 text-brand-600')}
            onClick={() => setFiltersOpen(o => !o)}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {hasFilters && <span className="bg-brand-500 text-white rounded-full h-4 w-4 text-[10px] flex items-center justify-center">!</span>}
          </button>
        }
      />

      <OnlineOrdersKpis tenantId={tid} />

      {/* Status tabs */}
      <div className="flex gap-1 flex-wrap">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setStatusBucket(t.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              statusBucket === t.key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <input
          className="input pl-9"
          placeholder="Search by order number, customer or phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {search && (
          <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearch('')}>
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Filters bar */}
      {filtersOpen && (
        <div className="card py-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="label">Date From</label>
              <input type="date" className="input py-1.5 text-sm w-36" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">Date To</label>
              <input type="date" className="input py-1.5 text-sm w-36" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <button className="btn-primary py-1.5 text-sm" onClick={applyFilters}>Apply</button>
              <button className="btn-secondary py-1.5 text-sm" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={debouncedSearch || hasFilters || statusBucket !== 'all' ? 'No online orders match' : 'No online orders yet'}
          description={
            debouncedSearch || hasFilters || statusBucket !== 'all'
              ? 'Try adjusting your search, status or date filters.'
              : 'Orders placed through your storefront will appear here.'
          }
          icon={<ShoppingBag className="h-8 w-8" />}
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Order #', 'Date', 'Customer', 'Total', 'Payment', 'Fulfilment', 'Delivery'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map(o => {
                  const pay = onlineOrderPaymentPill(o)
                  const ship = o.sales_order?.shipping_status
                  return (
                    <tr
                      key={o.id}
                      onClick={() => navigate(`/sell/online-orders/${o.id}`)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{o.order_number ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{displayDateTime(o.created_at)}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{o.customer_full_name}</p>
                        <p className="text-[11px] text-gray-400">{o.customer_phone}</p>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{format(o.total)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <span className={cn('badge', pay.cls)} title={pay.title}>{pay.label}</span>
                          {o.status === 'awaiting_transfer' && o.buyer_marked_paid && (
                            <span className="badge badge-blue text-[10px]">Buyer says paid</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {ship ? (
                          <span className="badge badge-gray">{titleCase(ship)}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {isPickup(o.delivery_zone_name)
                          ? <span className="badge badge-blue">Pickup</span>
                          : (o.delivery_zone_name || <span className="text-gray-300">—</span>)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
            {totalCount} order{totalCount !== 1 ? 's' : ''}
          </div>
          <div className="px-4 pb-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
